import { useEffect, useRef, useState } from 'react';
import { Fancybox } from '@fancyapps/ui';
import '@fancyapps/ui/dist/fancybox/fancybox.css';
import { uploadChatAttachment } from '../api/client.js';
import { ChatMessageBody } from './ChatMessageBody.jsx';
import { formatDateTime } from '../utils/formatDate.js';

const ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,application/pdf';

export function ChatPanel({
  messages,
  connected,
  connecting,
  onSend,
  onAttachmentUploaded,
  conversationId,
  attachmentAuth,
  disabled,
  title,
}) {
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const fileRef = useRef(null);
  const messagesRef = useRef(null);
  const galleryId = conversationId ? `chat-${conversationId}` : 'chat';

  useEffect(() => {
    const root = messagesRef.current;
    if (!root) return undefined;

    Fancybox.bind(root, '[data-fancybox]', {
      groupAll: false,
      placeFocusBack: false,
    });

    return () => {
      Fancybox.unbind(root);
      Fancybox.close(true);
    };
  }, [messages, galleryId]);

  const submit = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    setErr('');
    try {
      await onSend(text);
      setDraft('');
    } catch (ex) {
      setErr(ex.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const onFilePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !conversationId) return;

    setUploading(true);
    setErr('');
    try {
      const caption = draft.trim();
      const { message } = await uploadChatAttachment(conversationId, file, caption || undefined, attachmentAuth);
      if (caption) setDraft('');
      onAttachmentUploaded?.(message);
    } catch (ex) {
      setErr(ex.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const busy = sending || uploading;

  return (
    <div className="auth-card flat" style={{ display: 'flex', flexDirection: 'column', minHeight: 360 }}>
      <div className="row-actions" style={{ marginBottom: 8 }}>
        <strong>{title || 'Chat'}</strong>
        <span className={`muted small ${connected ? '' : connecting ? '' : 'err'}`}>
          {connected ? 'Connected' : connecting ? 'Connecting…' : 'Disconnected'}
        </span>
      </div>
      <div
        ref={messagesRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          border: '1px solid var(--border, #dee2e6)',
          borderRadius: 8,
          padding: 12,
          marginBottom: 12,
          maxHeight: 400,
        }}
      >
        {messages.length === 0 ? (
          <p className="muted small">No messages yet. Say hello!</p>
        ) : (
          messages.map((m) => (
            <div key={m.id} style={{ marginBottom: 10 }}>
              <div className="muted small">
                {m.sender_name || m.sender_type}
                {' · '}
                {m.created_at ? formatDateTime(m.created_at) : ''}
              </div>
              <ChatMessageBody message={m} attachmentAuth={attachmentAuth} galleryId={galleryId} />
            </div>
          ))
        )}
      </div>
      {err ? <p className="err small">{err}</p> : null}
      <form onSubmit={submit} className="row-actions">
        <input ref={fileRef} type="file" accept={ACCEPT} hidden onChange={(ev) => void onFilePick(ev)} />
        <button
          type="button"
          className="btn-secondary"
          title="Attach image or PDF"
          disabled={disabled || !connected || busy || !conversationId}
          onClick={() => fileRef.current?.click()}
        >
          📎
        </button>
        <input
          className="form-control"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={uploading ? 'Uploading…' : 'Type a message…'}
          disabled={disabled || !connected || busy}
          style={{ flex: 1 }}
        />
        <button type="submit" className="btn-primary" disabled={disabled || !connected || busy}>
          Send
        </button>
      </form>
      <p className="muted small" style={{ margin: '4px 0 0' }}>
        Images & PDF up to 10 MB
      </p>
    </div>
  );
}
